import { useTranslation } from "react-i18next";

import type { Bucket, Tally } from "../../services/http.js";

/**
 * Somebody's numbers — what a round scores, and the slices under it.
 *
 * # Out of how many, always
 *
 * Every figure is *"7 of 9"* rather than a percentage. A rate hides its sample,
 * and the sample is exactly what a reader here needs: Classic scores only the
 * active side, so nine is a plausible number of scored rounds for somebody who
 * sat through thirty-six. Showing the denominator is what stops "78%" being read
 * as seventy-eight percent of the evening.
 *
 * # Why this is a shared component and not two
 *
 * The recap and the career show the same seven numbers about the same person,
 * one game apart. They were one screen's private helpers until the career screen
 * needed them too, and phase 7's `SideBoard` is the precedent: two
 * implementations of one thing disagree, and the question is only when.
 */
export function TallyStats({ tally }: { tally: Tally }) {
  const { t } = useTranslation();

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
      <Stat label={t("tally.artist")} of={tally.artist} outOf={tally.rounds} />
      <Stat label={t("tally.title")} of={tally.title} outOf={tally.rounds} />
      <Stat label={t("tally.placed")} of={tally.slot} outOf={tally.rounds} />
      <Stat label={t("tally.exactYear")} of={tally.exact_year} outOf={tally.rounds} />
    </dl>
  );
}

function Stat({ label, of, outOf }: { label: string; of: number; outOf: number }) {
  const { t } = useTranslation();

  return (
    <div>
      <dt className="text-xs text-white/50">{label}</dt>
      <dd className="tabular-nums">
        {of} <span className="text-white/40">{t("tally.of", { outOf })}</span>
      </dd>
    </div>
  );
}

/**
 * A row of buckets, and what could not be filed into one.
 *
 * `unknown` is rendered rather than dropped for the reason the server counts it:
 * genre is a *current* fact about a track and a round is a historical one, so
 * some rounds simply cannot be classified. A statistic that silently shrank its
 * own denominator would read as a better score.
 *
 * `highlight` marks a bucket the caller wants pointed at — the career's best and
 * worst decades, which the server names by key.
 */
export function Slices({
  label,
  buckets,
  unknown,
  highlight,
}: {
  label: string;
  buckets: Bucket[];
  unknown?: Tally | undefined;
  /** Bucket keys to mark, if any. */
  highlight?: readonly string[] | undefined;
}) {
  const { t } = useTranslation();

  if (buckets.length === 0 && !unknown?.rounds) {
    return null;
  }
  return (
    <section className="mt-3">
      <h3 className="text-xs uppercase tracking-wide text-white/40">{label}</h3>
      <ul className="mt-1 flex flex-wrap gap-2 text-xs">
        {buckets.map((bucket) => (
          <li
            key={bucket.key}
            className={
              highlight?.includes(bucket.key)
                ? "rounded-lg bg-flash-500/15 px-2 py-1 tabular-nums"
                : "rounded-lg bg-stage-900 px-2 py-1 tabular-nums"
            }
          >
            {bucket.label}{" "}
            <span className="text-white/50">
              {bucket.tally.slot} {t("tally.of", { outOf: bucket.tally.rounds })}
            </span>
          </li>
        ))}
        {unknown && unknown.rounds > 0 && (
          <li className="rounded-lg bg-stage-900 px-2 py-1 text-white/40 tabular-nums">
            {t("tally.unclassified", { count: unknown.rounds })}
          </li>
        )}
      </ul>
    </section>
  );
}
